import {
  AppShell,
  Box,
  Burger,
  Divider,
  NavLink,
  rem,
  rgba,
  ScrollArea,
  Stack,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { IconApple, IconArticle, IconUser, IconWebhook } from "@tabler/icons-react";
import { CloudRainIcon } from "lucide-react";
import React from "react";
import { Link, useLocation } from "react-router-dom";

type JaloriesAppShellProps = {
  children: React.ReactNode;
};

export function JaloriesAppShell({ children }: JaloriesAppShellProps) {
  const theme = useMantineTheme();
  const location = useLocation();
  const [opened, { toggle, open, close }] = useDisclosure();
  const isWideScreen = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const shouldSidebarBeCollapsible = !isWideScreen; // Sidebar only collapses when we are below the desktop breakpoint

  // Clamp width so the navbar never covers main content on small screens
  const collapsibleSidebarWidth = shouldSidebarBeCollapsible ? `clamp(${rem(220)}, 70vw, ${rem(300)})` : rem(300);

  const sidebarOffset = shouldSidebarBeCollapsible && !opened ? "0px" : collapsibleSidebarWidth; // Keep main content offset only if sidebar is visible

  const appShellStyle = React.useMemo<Record<string, string>>(
    () => ({
      "--app-shell-navbar-width": collapsibleSidebarWidth,
      "--app-shell-navbar-offset": sidebarOffset, // when the sidebar is visible, we want to push the main content to the right by the width of the sidebar
    }),
    [collapsibleSidebarWidth, sidebarOffset],
  ); // Push responsive width/offset into Mantine's CSS variables

  const surfaceBorder = `1px solid ${rgba(theme.black, 0.08)}`;
  const subtleDashedBorder = `1px dashed ${rgba(theme.black, 0.1)}`;
  const hoverBackground = rgba(theme.black, 0.03);
  const activeBackground = rgba(theme.black, 0.06);

  // Responsive nav styles so sidebar content scales on smaller screens without ad-hoc conditionals
  const navLinkStyles = {
    root: {
      borderRadius: rem(16),
      background: theme.white,
      border: surfaceBorder,
      boxShadow: "none",
      transition: "background-color 150ms ease, transform 150ms ease",
      "&:hover": {
        transform: "translateY(-1px)",
        background: hoverBackground,
        borderColor: rgba(theme.black, 0.15),
      },
      "&[data-active]": {
        background: activeBackground,
        borderColor: rgba(theme.black, 0.2),
        transform: "translateY(-1px)",
      },
    },
    label: {
      textTransform: "uppercase",
      letterSpacing: rem(1.6),
      fontWeight: 700,
      fontSize: `clamp(${rem(12)}, calc(${rem(10)} + 0.6vw), ${rem(14)})`,
      color: theme.black,
    },
    description: {
      textTransform: "uppercase",
      letterSpacing: rem(1.1),
      fontSize: `clamp(${rem(9)}, calc(${rem(8)} + 0.4vw), ${rem(10)})`,
      fontWeight: 500,
      color: rgba(theme.black, 0.65),
    },
    leftSection: {
      color: rgba(theme.black, 0.55),
    },
  } as const;
  const iconSize = "1.1em"; // Keeps icon sizing proportional as fonts scale

  // Auto-open sidebar when enough room is available
  React.useEffect(() => {
    if (isWideScreen) {
      open();
    }
  }, [isWideScreen, open]);

  // Close collapsible sidebar after navigation (aka whenever the user clicks on any sidebar link)
  React.useEffect(() => {
    if (shouldSidebarBeCollapsible) {
      close();
    }
  }, [location.pathname, shouldSidebarBeCollapsible, close]);

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{ width: 300, breakpoint: "md", collapsed: { mobile: !opened, desktop: false } }}
      padding="lg"
      bg={theme.white}
      style={appShellStyle}
    >
      <AppShell.Header>
        {shouldSidebarBeCollapsible && <Burger opened={opened} onClick={toggle} size="sm" />}
      </AppShell.Header>
      <AppShell.Navbar
        px="lg"
        py="sm"
        style={{
          background: theme.white,
          borderRight: surfaceBorder,
          boxShadow: "none",
        }}
      >
        <AppShell.Section pb="md" px={0} style={{ borderBottom: subtleDashedBorder }}>
          <Stack gap={4}>
            <Title
              order={4}
              tt="uppercase"
              fw={900}
              c={rgba(theme.colors.deepOrange[7], 0.8)}
              style={{
                letterSpacing: rem(2),
                fontSize: `clamp(${rem(14)}, calc(${rem(12)} + 1vw), ${rem(18)})`,
              }}
            >
              Jalories
            </Title>
          </Stack>
        </AppShell.Section>
        <AppShell.Section grow component={ScrollArea} px={0} mt="lg">
          <Stack gap="sm">
            <NavLink
              component={Link}
              to="/add-meals"
              label="Add meals"
              active={location.pathname === "/add-meals"}
              leftSection={<IconArticle size={iconSize} stroke={1.5} />}
              description="Quickly add meal entries"
              styles={navLinkStyles}
            />
            <NavLink
              component={Link}
              to="/"
              label="Report"
              active={location.pathname === "/"}
              leftSection={<CloudRainIcon size={iconSize} />}
              description="See all logged meals"
              styles={navLinkStyles}
            />
            <Divider
              my="lg"
              label="Configure"
              labelPosition="center"
              styles={{
                label: {
                  textTransform: "uppercase",
                  letterSpacing: rem(1.4),
                  color: theme.black,
                  fontWeight: 700,
                },
              }}
              variant="dashed"
              color={rgba(theme.black, 0.2)}
            />
            <NavLink
              component={Link}
              to="/food-items"
              label="Food Items"
              active={location.pathname === "/food-items"}
              leftSection={<IconApple size={iconSize} stroke={1.5} />}
              description="Manage your food database"
              styles={navLinkStyles}
            />
            <NavLink
              component={Link}
              to="/targets"
              label="Targets"
              active={location.pathname === "/targets"}
              leftSection={<IconWebhook size={iconSize} stroke={1.5} />}
              description="Set your calorie and nutrient goals"
              styles={navLinkStyles}
            />
          </Stack>
        </AppShell.Section>
        <AppShell.Section mt="xl" px={0} style={{ borderTop: subtleDashedBorder }}>
          <Box pt="lg">
            <NavLink
              component="a"
              href="/profile"
              label="Profile"
              active={location.pathname === "/profile"}
              description="Update profile settings"
              leftSection={<IconUser size={iconSize} stroke={1.5} />}
              styles={navLinkStyles}
            />
          </Box>
        </AppShell.Section>
      </AppShell.Navbar>
      <AppShell.Main>{children}</AppShell.Main>
    </AppShell>
  );
}
